import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Building2, Sparkles, X, CornerDownLeft } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageContainer, PageHeader, Segmented, Skeleton, EmptyState, InlineError, SearchField, CompanyLogo, UserAvatar } from "@/components/common";
import { ScorePill, ValueRange, ConfidenceChip, TimingChip, kindLabel } from "@/components/intel/primitives";
import { useIntel } from "@/components/intel/context";

const EXISTING = [{ key: "all", label: "All" }, { key: "no", label: "New business" }, { key: "yes", label: "Existing customers" }];

export default function IntelCompaniesPage() {
  const navigate = useNavigate();
  const intel = useIntel();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("all");
  const [segment, setSegment] = useState("all");
  const [existing, setExisting] = useState("all");
  const [sort, setSort] = useState("score");
  const [nl, setNl] = useState("");
  const [nlBusy, setNlBusy] = useState(false);
  const [nlResult, setNlResult] = useState(null);

  const load = () => { setError(null); api.get("/intel/companies").then((r) => setData(r.data)).catch((e) => setError(errMsg(e))); };
  useEffect(() => { load(); }, [intel.version]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    let r = nlResult ? nlResult.rows : data?.rows || [];
    if (!nlResult) {
      if (country !== "all") r = r.filter((x) => x.company.country === country);
      if (segment !== "all") r = r.filter((x) => x.company.segment === segment);
      if (existing === "yes") r = r.filter((x) => x.company.existing_customer);
      if (existing === "no") r = r.filter((x) => !x.company.existing_customer);
      const s = q.trim().toLowerCase();
      if (s) r = r.filter((x) => `${x.company.name} ${x.company.city} ${x.company.segment} ${x.project_name}`.toLowerCase().includes(s));
      r = [...r];
      if (sort === "recent") r.sort((a, b) => String(b.lead_signal?.detected_date || "").localeCompare(String(a.lead_signal?.detected_date || "")));
      else if (sort === "value") r.sort((a, b) => (b.value_high || 0) - (a.value_high || 0));
      else if (sort === "name") r.sort((a, b) => a.company.name.localeCompare(b.company.name));
    }
    return r;
  }, [data, nlResult, country, segment, existing, q, sort]);

  const segments = useMemo(() => Array.from(new Set((data?.rows || []).map((r) => r.company.segment))).sort(), [data]);
  const countries = data?.countries || [];

  const runNl = async (e) => {
    e?.preventDefault?.();
    if (!nl.trim()) return;
    setNlBusy(true);
    try {
      const r = await api.post("/intel/search", { query: nl.trim() });
      setNlResult(r.data);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setNlBusy(false);
    }
  };

  return (
    <PageContainer wide data-testid="intel-companies-page">
      <PageHeader eyebrow="Sales Intelligence" title="Companies" description="The market map: every company with detected signals, ranked by opportunity score. Ask in plain language or filter." />
      {error ? <InlineError onRetry={load} testId="companies-error">{error}</InlineError> : null}

      <form onSubmit={runNl} className="mb-5">
        <div className="relative">
          <Sparkles className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-primary" />
          <input value={nl} onChange={(e) => setNl(e.target.value)} placeholder="Find new companies… e.g. “universities in Sweden with AV projects in the last 30 days” or “sports clubs building streaming”" className="h-12 w-full rounded-2xl border border-black/[0.09] bg-white pl-11 pr-28 text-[14px] shadow-[0_1px_2px_hsl(30_12%_18%/0.04)] placeholder:text-muted-foreground/70 hover:border-black/[0.16] focus-visible:border-primary/60 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/20" data-testid="intel-nl-search-input" />
          <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1.5">
            {nlResult ? <Button type="button" variant="ghost" size="sm" onClick={() => { setNlResult(null); setNl(""); }} data-testid="intel-nl-search-clear"><X className="h-3.5 w-3.5" /> Clear</Button> : null}
            <Button type="submit" size="sm" disabled={nlBusy || !nl.trim()} data-testid="intel-nl-search-submit">{nlBusy ? "Thinking…" : <>Search <CornerDownLeft className="h-3.5 w-3.5" /></>}</Button>
          </div>
        </div>
        {nlResult ? (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[12.5px] text-muted-foreground" data-testid="intel-nl-search-explanation">
            <Sparkles className="h-3.5 w-3.5 text-primary" /> {nlResult.filters?.explanation || "Interpreted your request"} · <span className="font-semibold tabular text-foreground">{nlResult.total}</span> companies
          </div>
        ) : null}
      </form>

      {!nlResult ? (
        <div className="mb-4 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Segmented items={EXISTING} value={existing} onChange={setExisting} testIdPrefix="companies-existing" />
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className="h-9 w-[150px] rounded-[10px] bg-white" data-testid="companies-country-filter"><SelectValue placeholder="Country" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All countries</SelectItem>{countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={segment} onValueChange={setSegment}>
              <SelectTrigger className="h-9 w-[190px] rounded-[10px] bg-white" data-testid="companies-segment-filter"><SelectValue placeholder="Segment" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All segments</SelectItem>{segments.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="h-9 w-[150px] rounded-[10px] bg-white" data-testid="companies-sort-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="score">Sort: score</SelectItem><SelectItem value="recent">Sort: latest signal</SelectItem><SelectItem value="value">Sort: value</SelectItem><SelectItem value="name">Sort: name</SelectItem></SelectContent>
            </Select>
          </div>
          <SearchField value={q} onChange={setQ} placeholder="Filter by name, city, segment" className="w-full xl:w-[280px]" testId="companies-search-input" />
        </div>
      ) : null}

      {!data ? (
        <div className="surface-flat divide-y divide-black/[0.05]">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="flex gap-4 px-5 py-4"><Skeleton className="h-10 w-10 rounded-xl" /><div className="flex-1"><Skeleton className="h-4 w-1/3" /><Skeleton className="mt-2 h-3 w-1/4" /></div></div>)}</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={Building2} title="No companies match" description="Try broader filters or a different question." action={<Button variant="outline" onClick={() => { setQ(""); setCountry("all"); setSegment("all"); setExisting("all"); setNlResult(null); }}>Clear</Button>} testId="companies-empty" />
      ) : (
        <div className="surface-flat overflow-hidden" data-testid="companies-list">
          <div className="hidden grid-cols-[1fr_200px_110px_140px_110px_32px] items-center gap-4 border-b border-black/[0.06] bg-[hsl(40_12%_98%)] px-5 py-2 md:grid">
            <span className="eyebrow text-[10px]">Company</span>
            <span className="eyebrow text-[10px]">Latest signal</span>
            <span className="eyebrow text-[10px]">Timing</span>
            <span className="eyebrow text-right text-[10px]">Est. range</span>
            <span className="eyebrow text-right text-[10px]">Score</span>
            <span />
          </div>
          <div className="divide-y divide-black/[0.05]">
            {rows.map((r) => (
              <button key={r.company_id} type="button" onClick={() => navigate(`/intel/companies/${r.company_id}`)} className="group grid w-full grid-cols-[1fr_auto] items-center gap-4 px-5 py-3.5 text-left transition-colors hover:bg-[hsl(40_12%_98%)] focus-visible:bg-[hsl(40_12%_98%)] focus-visible:outline-none md:grid-cols-[1fr_200px_110px_140px_110px_32px]" data-testid="companies-row">
                <div className="flex min-w-0 items-center gap-3.5">
                  <CompanyLogo name={r.company.name} domain={r.company.website} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><span className="truncate text-[14px] font-semibold tracking-[-0.01em]" data-testid="companies-row-name">{r.company.name}</span><span className={cn("hidden rounded-md px-1.5 py-px text-[10px] font-semibold uppercase tracking-[0.1em] sm:inline", r.kind === "expansion" ? "bg-[hsl(var(--stage-discovery)/0.12)] text-[hsl(var(--stage-discovery))]" : "bg-primary/10 text-primary")}>{kindLabel(r.kind)}</span></div>
                    <div className="truncate text-[12.5px] text-muted-foreground">{[r.company.segment, r.company.city, r.company.country].filter(Boolean).join(" · ")}</div>
                  </div>
                </div>
                <div className="md:hidden"><ScorePill score={r.score} /></div>
                <div className="hidden min-w-0 md:block"><div className="truncate text-[13px]">{r.lead_signal?.title || r.project_name}</div><div className="flex items-center gap-2 text-[11.5px] text-muted-foreground"><span className="font-mono">{fmtDate(r.lead_signal?.detected_date)}</span>{r.lead_signal ? <ConfidenceChip level={r.lead_signal.confidence} short className="px-1.5 py-0 text-[10px]" /> : null}</div></div>
                <div className="hidden md:block"><TimingChip timing={r.timing} /></div>
                <div className="hidden text-right text-[13px] md:block"><ValueRange label={r.value_label} /></div>
                <div className="hidden justify-end md:flex"><ScorePill score={r.score} /></div>
                <ArrowUpRight className="hidden h-4 w-4 shrink-0 text-muted-foreground/60 transition-colors group-hover:text-primary md:block" />
              </button>
            ))}
          </div>
        </div>
      )}
    </PageContainer>
  );
}
